import { type ComponentProps, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { mediaUrl } from '@/services';

type AdminImageProps = Pick<ComponentProps<'img'>, 'alt' | 'className'> & {
  src: string | null | undefined;
  /** Hovering or focusing the image floats a larger copy beside it. Meant for person photos. */
  preview?: boolean;
  /** Caption under the larger copy; defaults to `alt`. */
  previewLabel?: string;
  /** A second line under the caption, such as the name on file under the nickname. */
  previewDetail?: string | null;
  /** Adds an Open profile link under the larger copy, and keeps the preview open while the
   *  pointer moves into it so the link can be clicked. */
  profileHref?: string;
};

const PREVIEW_DELAY_MS = 300;

const INTERACTIVE_ANCESTOR =
  'a, button, label, input, select, textarea, summary, [role="button"], [role="option"], [role="menuitem"], [role="link"], [tabindex]:not([tabindex="-1"])';

/**
 * `<img>` wrapper that owns the one thing every call site would otherwise have to remember:
 * resolving a stored path through `mediaUrl` before rendering it, and rendering nothing for
 * an empty src rather than a broken-image icon.
 */
export function AdminImage({
  src,
  alt,
  className,
  preview = false,
  previewLabel,
  previewDetail,
  profileHref,
}: AdminImageProps) {
  const resolvedSrc = mediaUrl(src);
  if (!resolvedSrc) return null;

  // Lazy by default: a dashboard of person rows renders ~30 avatars, most of them below the
  // fold. An in-viewport image still loads immediately, so this costs the visible ones nothing.
  if (!preview) return <img src={resolvedSrc} alt={alt} className={className} loading="lazy" />;

  return (
    <PreviewImage
      src={resolvedSrc}
      alt={alt}
      className={className}
      label={previewLabel ?? alt}
      detail={previewDetail}
      profileHref={profileHref}
    />
  );
}

function PreviewImage({
  src,
  alt,
  className,
  label,
  detail,
  profileHref,
}: {
  src: string;
  alt: string | undefined;
  className: string | undefined;
  label: string | undefined;
  detail: string | null | undefined;
  profileHref: string | undefined;
}) {
  const { t } = useTranslation();
  // A tab stop of its own only when nothing around it takes focus already: a face inside a row
  // link or a picker option must not become a second, nested control.
  const [focusable, setFocusable] = useState(false);
  const measure = useCallback((node: HTMLImageElement | null) => {
    if (node) setFocusable(!node.parentElement?.closest(INTERACTIVE_ANCESTOR));
  }, []);

  return (
    // Radix Tooltip already ignores touch pointers, closes on Escape, pointer leave and scroll,
    // and lets the click through to the row underneath. Only a preview with a link stays open
    // under the pointer; a plain one must not cover the rows next to it.
    <TooltipProvider delayDuration={PREVIEW_DELAY_MS} disableHoverableContent={!profileHref}>
      <Tooltip>
        <TooltipTrigger asChild>
          <img
            ref={measure}
            src={src}
            alt={alt}
            className={cn(
              focusable && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              className
            )}
            loading="lazy"
            tabIndex={focusable ? 0 : undefined}
          />
        </TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          sideOffset={10}
          collisionPadding={16}
          aria-label={label || undefined}
          className={cn('max-w-none p-1.5 shadow-lg motion-reduce:animate-none', !profileHref && 'pointer-events-none')}
        >
          <img
            src={src}
            alt=""
            data-testid="admin-image-preview"
            className="h-56 w-56 rounded-lg border bg-muted object-cover"
          />
          {label && <p className="mt-1.5 max-w-56 truncate text-center text-sm font-medium">{label}</p>}
          {detail && detail !== label && (
            <p className="max-w-56 truncate text-center text-xs text-muted-foreground">{detail}</p>
          )}
          {profileHref && (
            <Link
              to={profileHref}
              className="mt-1.5 block rounded-md bg-accent py-1.5 text-center text-xs font-semibold text-accent-foreground hover:bg-accent/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('common.open_profile')}
            </Link>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
